(() => {
  if (customElements.get('amana-video-stories')) return;
  let activeStories;
  const registries = new Map();
  const shopifyUrl = value => {
    try { const url = new URL(value); return url.protocol === 'https:' && url.hostname === 'cdn.shopify.com'; } catch { return false; }
  };
  // Mesure d'audience vers le studio (agrégats anonymes, sans cookie ni identifiant), seulement si le client accepte l'analyse.
  const measure = { url: null, queue: [], timer: null, viewed: new Set(), engaged: false, players: new Set(), cartAt: 0 };
  // Accord d'analyse : vrai, faux, ou inconnu tant que l'API de consentement de Shopify n'est pas chargée.
  const measureConsent = () => window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.();
  // Inconnu : les événements attendent (nouvel essai 5 s plus tard) ; en fin de visite, sans accord, ils sont abandonnés.
  const flush = final => {
    clearTimeout(measure.timer); measure.timer = null;
    if (!measure.url || !measure.queue.length) return;
    const consent = measureConsent();
    if (consent === undefined && final !== true) { measure.timer = setTimeout(flush, 5000); return; }
    while (measure.queue.length) {
      const events = measure.queue.splice(0, 50);
      if (consent === true) try { navigator.sendBeacon(measure.url, new Blob([JSON.stringify({ v: 1, e: events })], { type: 'text/plain' })); } catch { /* Mesure facultative. */ }
    }
  };
  const track = event => {
    if (!measure.url) return;
    measure.queue.push(event);
    if (measure.queue.length >= 50) flush(); else measure.timer ??= setTimeout(flush, 5000);
  };
  // Ventes après vidéo (demande du 20/10/2026) : les vidéos lues depuis 7 jours restent sur l'appareil et sont recopiées
  // dans l'attribut privé du panier __amana_video (« id.minute,… »), que Shopify reporte sur la commande. Même accord
  // d'analyse que la mesure ; envoi par beacon en formulaire, hors des interceptions fetch du thème (cadeaux).
  const attribution = { pending: new Set(), timer: null, key: 'amana-video-views', keep: 7 * 1440 };
  const syncCart = final => {
    clearTimeout(attribution.timer); attribution.timer = null;
    if (!attribution.pending.size) return;
    const consent = measureConsent();
    if (consent === undefined && final !== true) { attribution.timer = setTimeout(syncCart, 5000); return; }
    if (consent !== true) { attribution.pending.clear(); try { localStorage.removeItem(attribution.key); } catch { /* Rien de gardé. */ } return; }
    const minute = Math.floor(Date.now() / 60000);
    let views = {};
    try { views = JSON.parse(localStorage.getItem(attribution.key)) || {}; } catch { /* Stockage indisponible : vidéos de cette page seulement. */ }
    for (const id of attribution.pending) views[id] = minute;
    attribution.pending.clear();
    const kept = Object.entries(views).filter(([id, at]) => /^\d{1,20}$/.test(id) && Number.isInteger(at) && minute - at <= attribution.keep && at <= minute).sort((a, b) => b[1] - a[1]).slice(0, 20);
    try { localStorage.setItem(attribution.key, JSON.stringify(Object.fromEntries(kept))); } catch { /* Stockage indisponible. */ }
    const body = new URLSearchParams({ 'attributes[__amana_video]': kept.map(([id, at]) => `${id}.${at}`).join(',') });
    const url = `${window.Shopify?.routes?.root || '/'}cart/update.js`;
    try { if (navigator.sendBeacon?.(url, new Blob([body.toString()], { type: 'application/x-www-form-urlencoded' }))) return; } catch { /* Repli ci-dessous. */ }
    fetch(url, { method: 'POST', body, keepalive: true, credentials: 'same-origin' }).catch(() => {});
  };
  const remember = id => {
    attribution.pending.add(id);
    clearTimeout(attribution.timer); attribution.timer = setTimeout(syncCart, 1500);
  };
  // Ajout au panier pendant la visite (fetch, XHR ou formulaire) : rapporté aux vidéos regardées sur la page.
  const cartAdd = value => { try { return /\/cart\/add(\.js|\.json)?$/.test(new URL(value, location.href).pathname); } catch { return false; } };
  const cartAdded = () => {
    const [player] = measure.players;
    if (!player || Date.now() - measure.cartAt < 2000) return;
    measure.cartAt = Date.now();
    track({ t: 'cart', p: player.dataset.productId, l: player.language, ids: [...measure.viewed] });
  };
  const startMeasure = url => {
    if (measure.url) return;
    measure.url = url;
    // Charge l'API de consentement si le thème ne l'a pas encore fait (méthode documentée par Shopify).
    if (!window.Shopify?.customerPrivacy) try { window.Shopify?.loadFeatures?.([{ name: 'consent-tracking-api', version: '0.1' }], () => {}); } catch { /* Sans API : rien n'est envoyé. */ }
    addEventListener('pagehide', () => { flush(true); syncCart(true); });
    document.addEventListener('visibilitychange', () => { if (document.hidden) { flush(true); syncCart(true); } });
    document.addEventListener('submit', event => { if (cartAdd(event.target?.getAttribute?.('action') || '')) cartAdded(); }, true);
    try { new PerformanceObserver(list => { if (list.getEntries().some(entry => cartAdd(entry.name))) cartAdded(); }).observe({ type: 'resource' }); } catch { /* Sans observateur : formulaires seulement. */ }
  };
  class AmanaVideoStories extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      const initialize = async () => {
        if (!this.isConnected || this.initialized) return;
        this.initialized = true;
        this.abort = new AbortController();
        const lifecycle = this.abort;
        const on = (element, event, callback) => element.addEventListener(event, callback, { signal: this.abort.signal });
        try { this.stories = JSON.parse(this.querySelector('[data-stories]').textContent); } catch { this.hidden = true; return; }
        const loadRegistry = async () => {
          if (!this.dataset.registryUrl) return;
          this.loadingRegistry = true;
          try {
            const url = new URL(this.dataset.registryUrl, location.href);
            if (url.origin !== location.origin && url.hostname !== 'cdn.shopify.com') throw new Error('Registry origin');
            if (!registries.has(url.href)) registries.set(url.href, fetch(url, { signal: AbortSignal.timeout(15000) }).then(response => {
              if (!response.ok) throw new Error('Registry unavailable');
              return response.json();
            }).catch(error => { registries.delete(url.href); throw error; }));
            const registry = await registries.get(url.href);
            if (!this.isConnected || lifecycle.signal.aborted || this.abort !== lifecycle) return;
            if (registry.version !== 1) throw new Error('Registry version');
            try { const url = new URL(registry.collect); if (url.protocol === 'https:' || ['127.0.0.1', 'localhost'].includes(url.hostname)) this.collectUrl = url.href; } catch { /* Sans mesure. */ }
            const fromRegistry = id => {
              const story = registry.videos?.[id];
              if (!story || !shopifyUrl(story.poster) || !shopifyUrl(story.thumbnail) || !story.sources?.length || story.sources.some(source => !shopifyUrl(source.url)) || !story.sources.some(source => source.format === 'mp4')) throw new Error('Invalid video');
              return { ...story, label: this.dataset.productTitle, scope: 'imported' };
            };
            const explicit = this.stories.length;
            let context = {};
            try { context = JSON.parse(this.querySelector('[data-product-context]').textContent) || {}; } catch { /* Sans contexte : ordre du registre. */ }
            if (!this.stories.length && this.dataset.migrationKey) {
              const ids = registry.groups?.[this.dataset.migrationKey]?.[this.dataset.productId];
              if (Array.isArray(ids)) this.stories = ids.map(fromRegistry);
            }
            // Règles et complément partagent le moteur de comparaison de la boutique.
            const complement = registry.complement;
            const hasComplement = Array.isArray(complement?.videos) && complement.max > 0;
            let matchProduct = null;
            if ((Array.isArray(registry.rules) && registry.rules.length) || hasComplement) {
              try {
                const rulesUrl = new URL(this.dataset.rulesUrl, location.href);
                if (rulesUrl.origin !== location.origin && rulesUrl.hostname !== 'cdn.shopify.com') throw new Error('Rules origin');
                ({ matchProduct } = await import(rulesUrl.href));
              } catch { /* Sans moteur : sélections importées seulement, complément par marque. */ }
              if (!this.isConnected || lifecycle.signal.aborted || this.abort !== lifecycle) return;
            }
            // Vidéos du modèle avant celles de toute la marque : taille > modèle > marque ; un pays précise.
            const tier = new Map();
            if (matchProduct && Array.isArray(registry.rules)) {
              const seen = new Set(this.stories.map(story => String(story.id)));
              const specificity = match => (match?.size ? 4 : 0) + (match?.model ? 2 : 0) + (match?.country ? 1 : 0);
              const matching = registry.rules.map((rule, index) => ({ rule, index })).filter(({ rule }) => rule?.enabled === true && Array.isArray(rule.videoIds) && matchProduct(context, rule.match).matches)
                .sort((a, b) => specificity(b.rule.match) - specificity(a.rule.match) || a.index - b.index);
              for (const { rule } of matching) {
                for (const id of rule.videoIds) {
                  try {
                    const story = fromRegistry(id);
                    if (seen.has(String(story.id))) continue;
                    this.stories.push({ ...story, scope: 'model', label: rule.label || context.title, ruleId: rule.id }); seen.add(String(story.id));
                    tier.set(String(story.id), rule.match?.model ? 0 : 1);
                  } catch { /* A broken rule media must not remove existing stories. */ }
                }
              }
            }
            // Langue du client : dans chaque niveau (sélection propre à la fiche, modèle, marque), ses vidéos passent devant ; ordre conservé sinon.
            const language = String(context.language || '').slice(0, 2).toLowerCase();
            this.language = language;
            const languageOf = new Map(Object.values(registry.videos || {}).map(video => [String(video.id), video.language || '']));
            const rank = story => !language ? 0 : languageOf.get(String(story.id)) === language ? 0 : languageOf.get(String(story.id)) ? 2 : 1;
            const fromModel = this.stories.splice(explicit);
            // La sélection importée pour cette fiche passe devant les règles de famille (ex. Petit Noé avant la règle Noé).
            // Vidéos de toute la marque : ordre tiré au hasard à chaque visite et une seule par influenceuse sur la fiche,
            // celles de la sélection et du modèle comprises.
            const shownCreators = new Set();
            this.stories.push(...fromModel.map((story, index) => { const level = story.scope === 'imported' ? -1 : tier.get(String(story.id)) ?? 0; return { story, tier: level, rank: rank(story) === 0 ? 0 : 1, order: level === 1 ? Math.random() : index }; })
              .sort((a, b) => a.tier - b.tier || a.rank - b.rank || a.order - b.order)
              .filter(({ story, tier: level }) => {
                if (!story.creator) return true;
                if (level === 1 && shownCreators.has(story.creator)) return false;
                shownCreators.add(story.creator); return true;
              }).map(entry => entry.story));
            // Complément unboxing : ciblé sur la langue du client (seulement / sauf), puis sa langue, même modèle, même marque ;
            // à égalité, tiré au hasard à chaque visite. Une seule vidéo par influenceuse sur la fiche, vidéos du modèle comprises.
            if (hasComplement) {
              const plain = value => String(value || '').normalize('NFKD').replace(/\p{M}/gu, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
              const brand = plain(context.brand) || (plain(context.vendor) !== 'amana' ? plain(context.vendor) : '');
              const seen = new Set(this.stories.map(story => String(story.id)));
              const candidates = [];
              for (const entry of complement.videos) {
                try {
                  const audience = Array.isArray(entry.only) ? entry.only.includes(language) : Array.isArray(entry.except) ? !entry.except.includes(language) : null;
                  if (audience === false) continue;
                  const story = fromRegistry(entry.id);
                  if (seen.has(String(story.id))) continue;
                  const subject = entry.match?.brand ? entry.match : null;
                  const sameModel = Boolean(subject?.model && matchProduct?.(context, { brand: subject.brand, model: subject.model }).matches);
                  const sameBrand = subject ? plain(subject.brand) === brand : Array.isArray(entry.brands) && Boolean(brand) && entry.brands.includes(brand);
                  candidates.push({ story, draw: Math.random(), target: audience ? 0 : 1, rank: rank(story), fit: sameModel ? 0 : sameBrand ? 1 : 2 });
                } catch { /* A broken complement media is skipped. */ }
              }
              candidates.sort((a, b) => a.target - b.target || a.rank - b.rank || a.fit - b.fit || a.draw - b.draw);
              const creators = new Set(this.stories.map(story => story.creator).filter(Boolean));
              // Demande du 18/10/2026 : fiche avec 1-2 vidéos de son modèle → 2 unboxings au plus, 3 ou plus → 1 (withModel) ; celles de toute la marque ne comptent pas.
              const withModel = Array.isArray(complement.withModel) && complement.withModel.length === 2 && complement.withModel.every(value => Number.isInteger(value) && value >= 0) ? complement.withModel : [2, 1];
              const own = this.stories.filter(story => tier.get(String(story.id)) !== 1).length;
              const limit = Math.min(complement.max, own ? withModel[own < 3 ? 0 : 1] : complement.max);
              let added = 0;
              for (const { story } of candidates) {
                if (added >= limit) break;
                if (story.creator && creators.has(story.creator)) continue;
                if (story.creator) creators.add(story.creator);
                this.stories.push({ ...story, scope: 'complement', label: complement.label || story.label }); added++;
              }
            }
          } catch { /* Explicit native stories remain usable if the registry is unavailable. */ }
          finally { if (this.abort === lifecycle) this.loadingRegistry = false; }
        };
        const pendingRegistry = loadRegistry();
        // Explicit media remain immediately usable while additive rules load in the background.
        if (!this.stories.length) await pendingRegistry;
        if (!this.isConnected || lifecycle.signal.aborted || this.abort !== lifecycle) return;
        if (!this.stories.length) { this.hidden = true; return; }
        this.hidden = false;
        this.labels = this.querySelector('[data-labels]').dataset;
        this.dialog = this.querySelector('[data-dialog]');
        this.video = this.querySelector('[data-player]');
        this.progress = this.querySelector('[data-progress]');
        this.status = this.querySelector('[data-status]');
        this.rail = this.querySelector('[data-rail]');
        this.surface = this.querySelector('[data-surface]');
        this.frame = this.querySelector('[data-frame]');
        this.mobile = matchMedia('(max-width:599px)');
        this.previews = [];
        this.previewObserver = new IntersectionObserver(entries => {
          for (const entry of entries) {
            const preview = entry.target; preview.dataset.visible = String(entry.isIntersecting);
            if (!entry.isIntersecting) preview.pause(); else this.playPreviews();
          }
        }, { threshold: .75 });
        const renderStories = start => {
        for (const [index, story] of [...this.stories.entries()].slice(start)) {
          const button = document.createElement('button');
          button.type = 'button'; button.className = 'amana-video__story';
          button.setAttribute('aria-label', `${this.labels.open} ${story.label} (${index + 1}/${this.stories.length})`);
          button.setAttribute('aria-haspopup', 'dialog'); button.setAttribute('aria-controls', this.id + '-dialog');
          const image = document.createElement('img');
          image.src = story.thumbnail; image.alt = ''; image.loading = 'lazy'; image.width = 96; image.height = 96;
          const cover = document.createElement('span'); cover.append(image); button.append(cover);
          // Aperçu animé : seulement le clip dédié de 3 s (≈ 60 Ko), jamais le MP4 complet de la vidéo.
          const clip = story.preview && shopifyUrl(story.preview) ? story.preview : null;
          if (clip && !matchMedia('(prefers-reduced-motion:reduce)').matches && !navigator.connection?.saveData) {
            const preview = document.createElement('video'); preview.muted = true; preview.playsInline = true; preview.loop = true; preview.preload = 'none'; preview.poster = story.thumbnail; preview.dataset.source = clip;
            preview.setAttribute('aria-hidden', 'true'); cover.append(preview); this.previews.push(preview); this.previewObserver.observe(preview);
            on(preview, 'error', () => { preview.hidden = true; });
          }
          on(button, 'click', () => this.open(index, button)); this.rail.append(button);
          // Intention : le survol (150 ms) ou le contact du doigt prépare la vidéo avant le clic.
          let intent;
          on(button, 'pointerenter', () => { clearTimeout(intent); intent = setTimeout(() => this.warm(index), 150); });
          on(button, 'pointerleave', () => clearTimeout(intent));
          on(button, 'pointerdown', () => { clearTimeout(intent); this.warm(index); });
        }
        for (const [index, button] of [...this.rail.children].entries()) button.setAttribute('aria-label', `${this.labels.open} ${this.stories[index].label} (${index + 1}/${this.stories.length})`);
        };
        renderStories(0);
        // Ronds bientôt visibles : seulement les listes HLS (quelques Ko) de la première vidéo.
        this.railObserver = new IntersectionObserver(entries => {
          if (!entries.some(entry => entry.isIntersecting)) return;
          this.railObserver.disconnect(); this.prefetchPlaylists(this.stories[0]);
        }, { rootMargin: '300px' });
        this.railObserver.observe(this.rail);
        this.dialog.id = this.id + '-dialog';
        this.querySelectorAll('[data-close]').forEach(button => on(button, 'click', () => this.close()));
        this.querySelectorAll('[data-step]').forEach(button => {
          button.hidden = this.stories.length < 2;
          on(button, 'click', () => this.select(this.index + Number(button.dataset.step)));
        });
        on(this.dialog, 'click', event => { if (event.target === this.dialog) { const bounds = this.dialog.getBoundingClientRect(); if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this.close(); } });
        on(this.dialog, 'cancel', event => { event.preventDefault(); this.close(); });
        on(this.dialog, 'close', () => { if (!this.dialog.open) this.cleanup(); });
        on(document, 'keydown', event => {
          if (!this.dialog.open) return;
          if (event.key === 'Escape') { event.preventDefault(); if (this.sheetOpen) this.closeSheet(); else this.close(); return; }
          if (!this.dialog.contains(event.target) || this.sheetOpen) return;
          if (event.target === this.progress) return;
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') { event.preventDefault(); this.select(this.index + (event.key === 'ArrowDown' ? 1 : -1)); }
        });
        on(this.querySelector('button[data-play]'), 'click', () => {
          if (this.dragged) { this.dragged = false; return; }
          if (this.video.paused) { if (this.video.error) this.video.load(); this.play(); } else this.video.pause();
        });
        on(this.querySelector('button[data-mute]'), 'click', () => { this.video.muted = !this.video.muted; this.updateControls(); });
        on(this.querySelector('[data-more]'), 'click', () => {
          const menu = this.querySelector('[data-menu]'); menu.hidden = !menu.hidden;
          this.querySelector('[data-more]').setAttribute('aria-expanded', String(!menu.hidden));
        });
        on(this.querySelector('[data-share]'), 'click', () => this.openSheet('share'));
        on(this.querySelector('[data-report]'), 'click', () => this.openSheet('report'));
        this.querySelectorAll('[data-sheet-close]').forEach(button => on(button, 'click', () => this.closeSheet()));
        on(this.querySelector('[data-shade]'), 'click', () => this.closeSheet());
        on(this.querySelector('[data-copy]'), 'click', async () => {
          const status = this.querySelector('[data-copy-status]');
          try { await navigator.clipboard.writeText(this.shareUrl()); status.textContent = this.labels.copied; }
          catch { status.textContent = this.labels.copyError; }
        });
        on(this.querySelector('[data-report-reason]'), 'change', () => this.updateReportLink());
        const gestureTarget = this.querySelector('button[data-play]');
        on(gestureTarget, 'pointerdown', event => {
          if (event.button !== 0 || this.sheetOpen || this.stories.length < 2) return;
          this.dragStart = event.clientY; this.dragged = false; gestureTarget.setPointerCapture(event.pointerId);
        });
        on(gestureTarget, 'pointermove', event => {
          if (this.dragStart == null) return;
          const distance = event.clientY - this.dragStart;
          if (Math.abs(distance) > 12) this.dragged = true;
          this.surface.style.transform = `translateY(${distance}px)`;
        });
        const finishDrag = event => {
          if (this.dragStart == null) return;
          const distance = event.clientY - this.dragStart; this.dragStart = null;
          this.surface.style.transform = '';
          if (event.type !== 'pointercancel' && Math.abs(distance) > 60) this.select(this.index + (distance < 0 ? 1 : -1));
        };
        on(gestureTarget, 'pointerup', finishDrag); on(gestureTarget, 'pointercancel', finishDrag);
        this.frame.addEventListener('wheel', event => {
          if (this.sheetOpen || this.stories.length < 2 || Math.abs(event.deltaY) < 12) return;
          event.preventDefault();
          if (Date.now() - (this.lastWheel || 0) < 450) return;
          this.lastWheel = Date.now(); this.select(this.index + (event.deltaY > 0 ? 1 : -1));
        }, { passive: false, signal: this.abort.signal });
        on(window, 'resize', () => {
          if (!this.dialog.open || this.isModal === this.mobile.matches) return;
          this.dialog.close(); this.showDialog(); this.select(this.index);
        });
        document.addEventListener('play', event => {
          if (activeStories && event.target !== activeStories.video && event.target instanceof HTMLMediaElement) event.target.pause();
        }, { capture: true, signal: this.abort.signal });
        on(this.progress, 'input', () => { if (Number.isFinite(this.video.duration)) this.video.currentTime = Number(this.progress.value) * this.video.duration / 100; });
        for (const event of ['play', 'pause', 'volumechange']) on(this.video, event, () => this.updateControls());
        on(this.video, 'timeupdate', () => {
          if (Number.isFinite(this.video.duration) && this.video.duration > 0) {
            const share = this.video.currentTime / this.video.duration * 100;
            while (this.viewing && this.viewing.quartile < 4 && share >= [25, 50, 75, 97][this.viewing.quartile]) track({ t: 'q', p: this.dataset.productId, id: this.viewing.id, q: [25, 50, 75, 100][this.viewing.quartile++] });
            this.progress.value = this.video.currentTime / this.video.duration * 100;
            this.querySelector('[data-fill]').style.width = `${this.progress.value}%`;
            this.querySelector('[data-time]').textContent = this.time(this.video.currentTime);
            this.querySelector('[data-duration]').textContent = this.time(this.video.duration);
          }
        });
        on(this.video, 'loadedmetadata', () => { this.progress.disabled = !Number.isFinite(this.video.duration); this.querySelector('[data-duration]').textContent = this.time(this.video.duration || 0); });
        on(this.video, 'playing', () => {
          this.status.hidden = true;
          if (this.stories.length > 1) this.prefetchPlaylists(this.stories[(this.index + 1) % this.stories.length], this.video.videoHeight || 720);
        });
        on(this.video, 'ended', () => this.stories.length > 1 ? this.select(this.index + 1) : this.updateControls());
        on(this.video, 'error', () => {
          const fallback = this.mp4Source;
          if (fallback && this.video.src !== fallback.url && this.dialog.open) { this.video.src = fallback.url; this.play(); return; }
          this.status.textContent = this.labels.error; this.status.hidden = false; this.updateControls();
        });
        on(document, 'visibilitychange', () => {
          if (document.hidden) { this.resume = this.dialog.open && !this.video.paused; this.video.pause(); this.previews.forEach(preview => preview.pause()); }
          else if (this.resume && this.dialog.open) { this.resume = false; this.play(); } else this.playPreviews();
        });
        on(window, 'pagehide', () => this.close());
        let requestedHandled = false;
        const openRequested = () => {
          if (requestedHandled) return;
          const requested = new URLSearchParams(location.search).get('amana_video');
          const requestedIndex = this.stories.findIndex(story => String(story.id) === requested);
          if (requestedIndex >= 0) { requestedHandled = true; if (!this.dialog.open) this.open(requestedIndex, this.rail.children[requestedIndex]); }
        };
        openRequested();
        pendingRegistry.then(() => {
          if (!this.isConnected || lifecycle.signal.aborted || this.abort !== lifecycle) return;
          renderStories(this.rail.children.length);
          this.querySelectorAll('[data-step]').forEach(button => { button.hidden = this.stories.length < 2; });
          if (this.dialog.open) this.querySelector('[data-count]').textContent = `${this.index + 1}/${this.stories.length}`;
          openRequested();
          if (this.collectUrl && /^\d+$/.test(this.dataset.productId || '')) {
            startMeasure(this.collectUrl); measure.players.add(this);
            track({ t: 'page', p: this.dataset.productId, l: this.language });
            // Rond vu à moitié au moins, une fois par visite.
            const seen = new IntersectionObserver(entries => {
              for (const entry of entries.filter(item => item.isIntersecting)) {
                seen.unobserve(entry.target);
                const index = [...this.rail.children].indexOf(entry.target), story = this.stories[index];
                if (story) track({ t: 'seen', p: this.dataset.productId, id: String(story.id), s: story.scope || 'native', i: index });
              }
            }, { threshold: .5 });
            for (const button of this.rail.children) seen.observe(button);
            on(window, 'pagehide', () => seen.disconnect());
          }
        });
      };
      if (this.querySelector('[data-labels]')) initialize();
      else { this.observer = new MutationObserver(() => { if (this.querySelector('[data-labels]')) { this.observer.disconnect(); initialize(); } }); this.observer.observe(this, { childList: true, subtree: true }); }
    }
    open(index, button) {
      if (activeStories && activeStories !== this) activeStories.close();
      activeStories = this; this.opener = button;
      document.querySelectorAll('video,audio').forEach(media => { if (media !== this.video) media.pause(); });
      this.showDialog();
      this.select(index); this.querySelector('[data-close]').focus();
    }
    sourceFor(story) {
      const width = this.video.getBoundingClientRect().width || (this.mobile.matches ? innerWidth : 369);
      const target = Math.min(1080, width * Math.min(window.devicePixelRatio || 1, 2));
      const mp4s = story.sources.filter(source => source.format === 'mp4').sort((a, b) => a.width - b.width);
      // Sans HLS natif (Firefox) : MP4 plafonné au débit annoncé par le navigateur quand il le donne (Mbit/s du nom Shopify).
      const connection = navigator.connection, budget = connection?.saveData || /2g/.test(connection?.effectiveType || '') ? 0 : connection?.downlink > 0 ? connection.downlink * .8 : Infinity;
      const rate = source => Number(/(\d+(?:\.\d+)?)Mbps/.exec(source.url)?.[1]) || 0;
      let mp4 = mp4s.find(source => source.width >= target) || mp4s.at(-1);
      if (mp4 && rate(mp4) > budget) mp4 = mp4s.filter(source => rate(source) <= budget).at(-1) || mp4s[0];
      const hls = story.sources.find(source => source.format === 'm3u8');
      return { mp4, source: hls && this.video.canPlayType('application/vnd.apple.mpegurl') ? hls : mp4 };
    }
    // Charge le début de la vidéo dans le lecteur encore fermé ; select() la reprend sans recommencer.
    warm(index) {
      if (this.dialog?.open || navigator.connection?.saveData) return;
      const story = this.stories?.[index]; if (!story) return;
      const { source } = this.sourceFor(story);
      if (!source || !/^https?:\/\/|^\/\//.test(source.url) || this.video.dataset.warm === source.url) return;
      this.video.preload = 'auto'; this.video.dataset.warm = source.url; this.video.src = source.url;
    }
    // Listes HLS d'une vidéo (lecture native : Safari, Chrome, Android) mises en cache ; avec `height`, seulement la variante
    // de cette hauteur (ou la plus proche en dessous) et son premier segment, pour que la vidéo suivante démarre aussitôt.
    prefetchPlaylists(story, height = 0) {
      if (!story || navigator.connection?.saveData || !this.video.canPlayType('application/vnd.apple.mpegurl')) return;
      const hls = story.sources.find(source => source.format === 'm3u8');
      if (!hls || !shopifyUrl(hls.url) || (this.prefetched ??= new Set()).has(hls.url)) return;
      this.prefetched.add(hls.url);
      const text = url => fetch(url, { priority: 'low' }).then(response => response.ok ? response.text() : '');
      text(hls.url).then(master => {
        const variants = []; let declared = 0;
        for (const line of master.split('\n')) {
          const value = line.trim(), resolution = /RESOLUTION=\d+x(\d+)/.exec(value);
          if (resolution) declared = Number(resolution[1]);
          else if (value && !value.startsWith('#')) { const url = new URL(value, hls.url).href; if (shopifyUrl(url)) variants.push({ url, height: declared }); }
        }
        if (!height) { variants.forEach(variant => text(variant.url).catch(() => {})); return; }
        const variant = variants.filter(item => item.height <= height).sort((a, b) => b.height - a.height)[0] || variants.sort((a, b) => a.height - b.height)[0];
        if (variant) return text(variant.url).then(playlist => {
          const first = playlist.split('\n').map(line => line.trim()).find(line => line && !line.startsWith('#'));
          const segment = first && new URL(first, variant.url).href;
          if (segment && shopifyUrl(segment)) return fetch(segment, { priority: 'low' });
        });
      }).catch(() => {});
    }
    select(index) {
      this.index = (index + this.stories.length) % this.stories.length;
      const story = this.stories[this.index];
      if (measure.players.has(this)) {
        const id = String(story.id);
        this.viewing = { id, quartile: 0 };
        if (!measure.engaged) { measure.engaged = true; track({ t: 'engaged', p: this.dataset.productId, l: this.language }); }
        track({ t: 'view', p: this.dataset.productId, id, s: story.scope || 'native', i: this.index }); measure.viewed.add(id);
        if (/^\d{1,20}$/.test(id)) remember(id);
      }
      const { source, mp4 } = this.sourceFor(story);
      const warmed = Boolean(source) && this.video.dataset.warm === source.url && this.video.getAttribute('src') === source.url;
      delete this.video.dataset.warm;
      if (!warmed) { this.video.pause(); this.video.removeAttribute('src'); this.video.load(); }
      this.progress.value = 0; this.progress.disabled = true; this.resume = false; this.status.hidden = true;
      this.querySelector('[data-title]').textContent = story.label;
      // Vignette déjà en mémoire tout de suite ; l'image pleine résolution la remplace une fois chargée.
      this.querySelector('[data-blur]').style.backgroundImage = `url("${story.thumbnail.replace(/["\\\n\r]/g, '')}")`;
      this.querySelector('[data-fill]').style.width = '0%';
      this.querySelector('[data-time]').textContent = '0:00';
      this.querySelector('[data-duration]').textContent = '0:00';
      this.querySelector('[data-menu]').hidden = true;
      this.querySelector('[data-more]').setAttribute('aria-expanded', 'false');
      this.querySelector('[data-count]').textContent = `${this.index + 1}/${this.stories.length}`;
      this.video.setAttribute('aria-label', story.label); this.video.poster = story.thumbnail;
      const full = new Image();
      full.onload = () => { if (this.stories[this.index] === story) this.video.poster = story.poster; };
      full.src = story.poster;
      this.mp4Source = mp4;
      if (!source || !/^https?:\/\/|^\/\//.test(source.url)) { this.status.textContent = this.labels.error; this.status.hidden = false; return; }
      if (!warmed) this.video.src = source.url;
      this.play();
    }
    play() { this.video.play().catch(() => this.updateControls()); }
    updateControls() {
      const play = this.querySelector('button[data-play]');
      play.dataset.playing = String(!this.video.paused);
      play.setAttribute('aria-label', this.video.paused ? this.labels.play : this.labels.pause);
      const mute = this.querySelector('button[data-mute]');
      mute.setAttribute('aria-pressed', String(this.video.muted));
      mute.setAttribute('aria-label', this.video.muted ? this.labels.unmute : this.labels.mute);
    }
    playPreviews() {
      if (activeStories || document.hidden || !this.previews) return;
      for (const preview of this.previews) {
        if (preview.dataset.visible !== 'true' || preview.hidden) continue;
        if (!preview.src) preview.src = preview.dataset.source;
        preview.play().catch(() => {});
      }
    }
    showDialog() {
      this.isModal = this.mobile.matches;
      if (this.isModal) this.dialog.showModal(); else this.dialog.show();
      document.documentElement.classList.toggle('amana-video-open', this.isModal);
    }
    time(seconds) { return `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`; }
    shareUrl() {
      const url = new URL(location.pathname, location.origin);
      const current = new URL(location.href);
      for (const key of ['preview_theme_id', 'view']) if (current.searchParams.has(key)) url.searchParams.set(key, current.searchParams.get(key));
      url.searchParams.set('amana_video', this.stories[this.index].id); return url.href;
    }
    openSheet(name) {
      this.querySelector('[data-menu]').hidden = true;
      this.querySelector('[data-more]').setAttribute('aria-expanded', 'false');
      this.sheetWasPlaying = !this.video.paused; this.video.pause(); this.sheetOpen = name;
      this.querySelector('[data-shade]').hidden = false;
      this.querySelector(`[data-${name}-sheet]`).hidden = false;
      if (name === 'share') {
        const url = encodeURIComponent(this.shareUrl());
        const platforms = { facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`, linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`, whatsapp: `https://wa.me/?text=${url}`, twitter: `https://twitter.com/intent/tweet?url=${url}` };
        for (const link of this.querySelectorAll('[data-platform]')) link.href = platforms[link.dataset.platform];
      } else this.updateReportLink();
      this.querySelector(`[data-${name}-sheet] [data-sheet-close]`).focus();
    }
    updateReportLink() {
      const link = this.querySelector('[data-report-contact]');
      this.contactPath ||= link.getAttribute('href');
      const url = new URL(this.contactPath, location.origin);
      url.searchParams.set('video', this.stories[this.index].id);
      url.searchParams.set('reason', this.querySelector('[data-report-reason]').value); link.href = url.href;
    }
    closeSheet() {
      const previous = this.sheetOpen; this.sheetOpen = null;
      this.querySelector('[data-shade]').hidden = true;
      this.querySelector('[data-share-sheet]').hidden = true;
      this.querySelector('[data-report-sheet]').hidden = true;
      if (this.sheetWasPlaying && this.dialog.open) this.play(); this.sheetWasPlaying = false;
      if (previous) this.querySelector(previous === 'share' ? '[data-share]' : '[data-more]').focus();
    }
    close() { if (this.dialog?.open) this.dialog.close(); this.cleanup(); }
    cleanup() {
      this.resume = false; this.dragStart = null;
      if (this.sheetOpen) { this.sheetWasPlaying = false; this.closeSheet(); }
      this.video?.pause(); this.video?.removeAttribute('src'); if (this.video) delete this.video.dataset.warm; this.video?.load();
      if (activeStories === this) { activeStories = null; document.documentElement.classList.remove('amana-video-open'); }
      if (this.opener?.isConnected) this.opener.focus();
      for (const component of document.querySelectorAll('amana-video-stories')) component.playPreviews?.();
    }
    disconnectedCallback() { this.close(); this.abort?.abort(); this.observer?.disconnect(); this.previewObserver?.disconnect(); this.railObserver?.disconnect(); this.rail?.replaceChildren(); this.initialized = false; }
  }
  customElements.define('amana-video-stories', AmanaVideoStories);
})();
